// storefront-config — runtime marketplace display config for the embed.
//
// TD-308 / GH #45: the shipping=$0 checkout label is operator-configurable
// (DB-backed marketplace_settings.free_shipping_label, DEFAULT '$0.00' since
// migration 144 — the standing operator rule retired the legacy word as a
// storefront label). This module fetches the public-safe settings subset
// (currency + the label) once on mount and exposes it via React context so the
// checkout components (CartSummary, OrderReviewPanel, OrderDetailView) can
// render the operator-chosen label instead of a hardcoded "FREE".
//
// Fail-open by design: the context DEFAULT_CONFIG matches the backend DB
// defaults, so the embed renders correctly before the fetch resolves AND on any
// fetch failure (network error, 5xx, malformed payload). The checkout never
// blanks out over a transient blip — the same fail-open contract the backend
// handler uses (HandlePublicSettings).

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { embedApiFetch } from "../../embed-shared/api-client";

export interface StorefrontConfig {
  currency: string;
  /** Display label shown when shipping === 0 (operator-configurable). */
  freeShippingLabel: string;
  // StoreContent marketing fields — optional; absent = not configured (fail-open).
  // snake_case mirrors the wire + the shared StoreContent TS interface (no translation layer).
  hero_headline?: string;
  hero_subheadline?: string;
  hero_cta_text?: string;
  hero_cta_url?: string;
  catalog_intro?: string;
  about_text?: string;
  contact_email?: string;
  contact_phone?: string;
  footer_text?: string;
  seo_title?: string;
  seo_description?: string;
  /**
   * Marketplace-wide placeholder product image (TD-324). Used ONLY for the
   * broken-image fallback — a product with no images is substituted
   * server-side. Undefined from mount until the provider's fetch resolves
   * with a configured value (and stays undefined forever on fetch failure or
   * when the operator hasn't configured one) — consumers must degrade to the
   * branded box rather than assume it is present at mount.
   */
  placeholder_image_url?: string;
  /**
   * Operator-uploaded hero image CDN URL (Task 6, spec H1). Present ONLY
   * when the operator has configured one — the Go field carries
   * `,omitempty`, so the wire key is ABSENT (not "") when unset, and that
   * distinction is load-bearing: absent means "render the product montage
   * instead" (H1's two-art-sources switch). Undefined until the provider's
   * fetch resolves with a configured value.
   */
  hero_image_url?: string;
}

/** Defaults match the marketplace_settings DDL defaults (migrations 028 + 076 + 144). */
export const DEFAULT_STOREFRONT_CONFIG: StorefrontConfig = {
  currency: "USD",
  freeShippingLabel: "$0.00",
};

/**
 * Context with a non-null default so consumers (the checkout components) work
 * even when rendered WITHOUT a provider — e.g. in their isolated unit tests,
 * or if a future mount path skips the provider. The provider overrides with
 * the live operator config.
 */
const StorefrontConfigContext = createContext<StorefrontConfig>(
  DEFAULT_STOREFRONT_CONFIG,
);

/** Read the storefront display config (currency + free-shipping label). */
export function useStorefrontConfig(): StorefrontConfig {
  return useContext(StorefrontConfigContext);
}

interface PublicSettingsWire {
  currency?: string | null;
  free_shipping_label?: string | null;
  // StoreContent marketing fields (Task 1) — snake_case, nullable on the wire.
  hero_headline?: string | null;
  hero_subheadline?: string | null;
  hero_cta_text?: string | null;
  hero_cta_url?: string | null;
  catalog_intro?: string | null;
  about_text?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  footer_text?: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
  // TD-324: non-nullable "" when unset per the Task 8 contract; typed nullable
  // anyway to match its siblings' tolerant wire posture (costs nothing, str()
  // collapses null the same as "").
  placeholder_image_url?: string | null;
  // Task 6 (spec H1): omitempty on the Go side, so this key is ABSENT (not
  // null, not "") when unset — typed nullable anyway for the same tolerant
  // posture as its siblings; str() collapses absent/null/"" identically.
  hero_image_url?: string | null;
}

/**
 * Fail-open normalizer for optional marketing strings: a non-empty string
 * passes through; null/undefined/empty-string all collapse to undefined so the
 * config carries the field only when the operator actually configured it.
 */
const str = (v: string | null | undefined): string | undefined =>
  typeof v === "string" && v ? v : undefined;

interface StorefrontConfigProviderProps {
  children: ReactNode;
  /**
   * Optional injected value. When provided, the provider serves this value
   * directly and does NOT fetch — used by tests/storybook to pin a known
   * config without network. When omitted, the provider fetches the public
   * marketplace settings once on mount.
   */
  value?: StorefrontConfig;
}

/**
 * Fetches the public marketplace settings once and provides the live
 * operator-configured display config to descendants. Network/parse failures
 * are swallowed — the defaults remain in place (fail-open).
 */
export function StorefrontConfigProvider({ children, value }: StorefrontConfigProviderProps) {
  const [config, setConfig] = useState<StorefrontConfig>(
    value ?? DEFAULT_STOREFRONT_CONFIG,
  );

  useEffect(() => {
    // Injected value (tests/storybook): skip the fetch entirely.
    if (value !== undefined) return;
    let cancelled = false;
    embedApiFetch<PublicSettingsWire>("/api/v1/marketplace/settings/public")
      .then((data) => {
        if (cancelled || !data) return;
        setConfig({
          currency:
            typeof data.currency === "string" && data.currency
              ? data.currency
              : DEFAULT_STOREFRONT_CONFIG.currency,
          freeShippingLabel:
            typeof data.free_shipping_label === "string" && data.free_shipping_label
              ? data.free_shipping_label
              : DEFAULT_STOREFRONT_CONFIG.freeShippingLabel,
          // StoreContent marketing fields — fail-open: absent on the wire or empty
          // → undefined on the config (consumers treat undefined as "not configured").
          hero_headline: str(data.hero_headline),
          hero_subheadline: str(data.hero_subheadline),
          hero_cta_text: str(data.hero_cta_text),
          hero_cta_url: str(data.hero_cta_url),
          catalog_intro: str(data.catalog_intro),
          about_text: str(data.about_text),
          contact_email: str(data.contact_email),
          contact_phone: str(data.contact_phone),
          footer_text: str(data.footer_text),
          seo_title: str(data.seo_title),
          seo_description: str(data.seo_description),
          placeholder_image_url: str(data.placeholder_image_url),
          hero_image_url: str(data.hero_image_url),
        });
      })
      .catch(() => {
        // Fail-open: keep DEFAULT_STOREFRONT_CONFIG. The backend also fails
        // open; a transient error leaves the checkout showing the default
        // shipping $0 label.
      });
    return () => {
      cancelled = true;
    };
  }, [value]);

  return (
    <StorefrontConfigContext.Provider value={value ?? config}>
      {children}
    </StorefrontConfigContext.Provider>
  );
}
